import React, { useState, useEffect, useRef, useCallback } from 'react';
import { string, bool } from 'prop-types';
import { compose } from 'redux';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldLocationAutocompleteInput,
  IconClose,
} from '../../components';
import {
  requiredAndNonEmptyString,
  autocompleteSearchRequired,
  autocompletePlaceSelected,
  composeValidators,
} from '../../util/validators';

import css from './CheckBoatForm.module.css';

const identity = v => v;

const SectionIcon = ({ children }) => (
  <span className={css.sectionIcon} aria-hidden="true">
    {children}
  </span>
);

const ConditionIcon = () => (
  <SectionIcon>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12l2 2 4-4" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  </SectionIcon>
);

const UsageIcon = () => (
  <SectionIcon>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  </SectionIcon>
);

const LocationIcon = () => (
  <SectionIcon>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  </SectionIcon>
);

const PhotosIcon = () => (
  <SectionIcon>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="12" cy="12" r="3.5" />
    </svg>
  </SectionIcon>
);

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const PhotoThumb = ({ photo, onRemove }) => (
  <li className={css.photoThumb}>
    <button
      type="button"
      className={css.photoRemove}
      onClick={() => onRemove(photo.id)}
      aria-label="Remove photo"
    >
      <IconClose className={css.photoRemoveIcon} />
    </button>
    <img src={photo.preview} alt="Inspection photo" className={css.photoThumbImage} />
  </li>
);

const CheckBoatFormComponent = props => {
  const [photos, setPhotos] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const photoIdRef = useRef(0);

  const makePhoto = useCallback(file => {
    photoIdRef.current += 1;
    return {
      id: `p_${photoIdRef.current}`,
      file,
      preview: URL.createObjectURL(file),
    };
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
    setShowCamera(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setCameraReady(true);
      }
    } catch (err) {
      setCameraError(err?.message || 'Camera unavailable');
      setShowCamera(false);
    }
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !cameraReady) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      blob => {
        if (!blob) return;
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setPhotos(prev => [...prev, makePhoto(file)]);
      },
      'image/jpeg',
      0.9
    );
  }, [cameraReady, makePhoto]);

  const addFiles = useCallback(
    files => {
      const list = Array.from(files || []);
      if (!list.length) return;
      setPhotos(prev => [...prev, ...list.map(makePhoto)]);
    },
    [makePhoto]
  );

  const {
    getRootProps: getDropzoneRootProps,
    getInputProps: getDropzoneInputProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    accept: 'image/*',
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: addFiles,
  });

  const removePhoto = useCallback(id => {
    setPhotos(prev => {
      const removed = prev.find(p => p.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter(p => p.id !== id);
    });
  }, []);

  // Cleanup on unmount: stop the camera and free preview URLs.
  // We intentionally read the latest photos via a ref so the effect runs once.
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      photosRef.current.forEach(p => URL.revokeObjectURL(p.preview));
    };
  }, []);

  // Inject photos into the submitted values at submit-time instead of
  // mirroring them into form state on every change (which used to cause an
  // infinite render loop via final-form's notify-on-change).
  const originalOnSubmit = props.onSubmit;
  const onSubmitWithPhotos = (values, formApi, callback) =>
    originalOnSubmit(
      { ...values, images: photos.map(p => p.file) },
      formApi,
      callback
    );

  return (
    <FinalForm
      {...props}
      onSubmit={onSubmitWithPhotos}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          values,
          form,
          invalid,
          handleSubmit,
          intl,
          checkBoatInProgress,
          checkBoatError,
        } = fieldRenderProps;

        const damageLabel = intl.formatMessage({ id: 'damageBoatForm.damageLabel' });
        const damagePlaceholder = intl.formatMessage({ id: 'damageBoatForm.damagePlaceholder' });
        const damageRequiredMessage = intl.formatMessage({ id: 'damageBoatForm.damageRequired' });
        const damageRequired = requiredAndNonEmptyString(damageRequiredMessage);

        const hoursLabel = intl.formatMessage({ id: 'hoursBoatForm.hoursLabel' });
        const hoursPlaceholder = intl.formatMessage({ id: 'hoursBoatForm.hoursPlaceholder' });
        const hoursRequiredMessage = intl.formatMessage({ id: 'hoursBoatForm.hoursRequired' });
        const hoursRequired = requiredAndNonEmptyString(hoursRequiredMessage);

        const levelLabel = intl.formatMessage({ id: 'levelBoatForm.levelLabel' });
        const levelPlaceholder = intl.formatMessage({ id: 'levelBoatForm.levelPlaceholder' });
        const levelRequiredMessage = intl.formatMessage({ id: 'levelBoatForm.levelRequired' });
        const levelRequired = requiredAndNonEmptyString(levelRequiredMessage);

        const notesLabel = intl.formatMessage({ id: 'CheckBoatForm.notesLabel' });
        const notesPlaceholder = intl.formatMessage({ id: 'CheckBoatForm.notesPlaceholder' });
        const notesRequiredMessage = intl.formatMessage({ id: 'CheckBoatForm.notesRequired' });
        const notesRequired = requiredAndNonEmptyString(notesRequiredMessage);

        const locationLabel = intl.formatMessage({ id: 'CheckBoatForm.locationLabel' });
        const locationPlaceholder = intl.formatMessage({ id: 'CheckBoatForm.locationPlaceholder' });
        const addressRequiredMessage = intl.formatMessage({ id: 'CheckBoatForm.addressRequired' });
        const addressNotRecognizedMessage = intl.formatMessage({
          id: 'CheckBoatForm.addressNotRecognized',
        });

        const errorMessage = checkBoatError ? (
          <p className={css.error}>
            <FormattedMessage id="CheckBoatForm.checkBoatError" />
          </p>
        ) : null;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = checkBoatInProgress;
        const submitDisabled = invalid || submitInProgress || photos.length === 0;
        const photoCount = photos.length;

        return (
          <Form
            className={classes}
            onSubmit={async submitValues => {
              await handleSubmit(submitValues, form);
              photos.forEach(p => URL.revokeObjectURL(p.preview));
              setPhotos([]);
            }}
            enforcePagePreloadFor="OrderDetailsPage"
          >
            {/* Condition */}
            <section className={css.section}>
              <header className={css.sectionHeader}>
                <ConditionIcon />
                <div>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage id="CheckBoatForm.conditionSection" />
                  </h3>
                  <p className={css.sectionDescription}>
                    <FormattedMessage id="CheckBoatForm.conditionSectionDesc" />
                  </p>
                </div>
              </header>

              <div className={css.fieldGroup}>
                <FieldTextInput
                  className={css.fieldGroupItem}
                  type="textarea"
                  name="damage"
                  id={formId ? `${formId}.damage` : 'damage'}
                  label={damageLabel}
                  placeholder={damagePlaceholder}
                  validate={damageRequired}
                />
                <FieldTextInput
                  className={css.fieldGroupItem}
                  type="textarea"
                  name="notes"
                  id={formId ? `${formId}.notes` : 'notes'}
                  label={notesLabel}
                  placeholder={notesPlaceholder}
                  validate={notesRequired}
                />
              </div>
            </section>

            {/* Usage */}
            <section className={css.section}>
              <header className={css.sectionHeader}>
                <UsageIcon />
                <div>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage id="CheckBoatForm.usageSection" />
                  </h3>
                  <p className={css.sectionDescription}>
                    <FormattedMessage id="CheckBoatForm.usageSectionDesc" />
                  </p>
                </div>
              </header>

              <div className={css.fieldGroup}>
                <FieldTextInput
                  className={css.fieldGroupItem}
                  type="text"
                  inputMode="numeric"
                  name="hours"
                  id={formId ? `${formId}.hours` : 'hours'}
                  label={hoursLabel}
                  placeholder={hoursPlaceholder}
                  validate={hoursRequired}
                />
                <FieldTextInput
                  className={css.fieldGroupItem}
                  type="text"
                  name="level"
                  id={formId ? `${formId}.level` : 'level'}
                  label={levelLabel}
                  placeholder={levelPlaceholder}
                  validate={levelRequired}
                />
              </div>
            </section>

            {/* Location */}
            <section className={css.section}>
              <header className={css.sectionHeader}>
                <LocationIcon />
                <div>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage id="CheckBoatForm.locationSection" />
                  </h3>
                  <p className={css.sectionDescription}>
                    <FormattedMessage id="CheckBoatForm.locationSectionDesc" />
                  </p>
                </div>
              </header>

              <FieldLocationAutocompleteInput
                rootClassName={css.locationAddressWrapper}
                className={css.locationAddress}
                inputClassName={css.locationAutocompleteInput}
                iconClassName={css.locationAutocompleteInputIcon}
                predictionsClassName={css.predictionsRoot}
                validClassName={css.validLocation}
                name="location"
                label={locationLabel}
                placeholder={locationPlaceholder}
                useDefaultPredictions={false}
                format={identity}
                valueFromForm={values.location}
                validate={composeValidators(
                  autocompleteSearchRequired(addressRequiredMessage),
                  autocompletePlaceSelected(addressNotRecognizedMessage)
                )}
              />
            </section>

            {/* Photos */}
            <section className={css.section}>
              <header className={css.sectionHeader}>
                <PhotosIcon />
                <div>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage id="CheckBoatForm.documentationSection" />
                  </h3>
                  <p className={css.sectionDescription}>
                    <FormattedMessage id="CheckBoatForm.documentationSectionDesc" />
                  </p>
                </div>
              </header>

              {showCamera ? (
                <div className={css.cameraContainer}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={css.cameraVideo}
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />

                  {photoCount > 0 ? (
                    <div className={css.cameraBadge}>
                      <FormattedMessage
                        id="CheckBoatForm.photosCaptured"
                        values={{ count: photoCount }}
                      />
                    </div>
                  ) : null}

                  <div className={css.cameraControls}>
                    <button
                      type="button"
                      onClick={capturePhoto}
                      className={css.captureButton}
                      disabled={!cameraReady}
                      aria-label="Capture photo"
                    >
                      <span className={css.captureRing} />
                      <span className={css.captureCore} />
                    </button>
                    <button
                      type="button"
                      onClick={stopCamera}
                      className={css.closeCamera}
                    >
                      <FormattedMessage id="CheckBoatForm.closeCamera" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={startCamera}
                    className={classNames(css.photoActionButton, css.photoActionPrimary)}
                  >
                    <CameraIcon />
                    <FormattedMessage id="CheckBoatForm.openCamera" />
                  </button>

                  <div className={css.photoDivider}>
                    <span className={css.photoDividerLabel}>
                      <FormattedMessage id="CheckBoatForm.or" />
                    </span>
                  </div>

                  <div
                    {...getDropzoneRootProps({
                      className: classNames(css.dropzone, {
                        [css.dropzoneActive]: isDragActive,
                      }),
                      onClick: openFilePicker,
                      role: 'button',
                      tabIndex: 0,
                    })}
                  >
                    <input {...getDropzoneInputProps()} />
                    <UploadIcon />
                    <p className={css.dropzoneTitle}>
                      <FormattedMessage
                        id="FieldDropzone.addImagesMessage"
                        values={{
                          browseMessage: (
                            <span className={css.dropzoneBrowse}>
                              <FormattedMessage id="FieldDropzone.browseMessage" />
                            </span>
                          ),
                        }}
                      />
                    </p>
                    <p className={css.dropzoneHint}>
                      <FormattedMessage id="CheckBoatForm.imagesLabel" />
                    </p>
                  </div>

                  {cameraError ? (
                    <p className={css.cameraErrorMessage}>{cameraError}</p>
                  ) : null}
                </>
              )}

              {photoCount > 0 ? (
                <ul className={css.photoGrid}>
                  {photos.map(photo => (
                    <PhotoThumb key={photo.id} photo={photo} onRemove={removePhoto} />
                  ))}
                </ul>
              ) : null}

            </section>

            {errorMessage}

            <div className={css.submitBar}>
              <p className={css.helperText}>
                <FormattedMessage id="CheckBoatForm.helperText" />
              </p>
              <PrimaryButton
                type="submit"
                className={css.submitButton}
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="CheckBoatForm.submitButtonText" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

CheckBoatFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
};

CheckBoatFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const CheckBoatForm = compose(injectIntl)(CheckBoatFormComponent);

CheckBoatForm.displayName = 'CheckBoatForm';

export default CheckBoatForm;
